/**
 * Intake: the queue, a file and everything hung off it, and processing runs.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import * as intakeApi from '../api/Intake';
import { putToStorage } from '../api/storage';
import { parsed } from '../api/parse';
import type { ModuleCode } from '../features/modules/modules';
import {
	classificationPreviewSchema,
	fileEventSchema,
	clientRecordSchema,
	fileClassificationRuleRecordSchema,
	fileColumnsSchema,
	fileRecordSchema,
	fileSampleSchema,
	fileWithClassifiedAtSchema,
	intakeQueueEntrySchema,
	latestIngestSchema,
	reapplyResultSchema,
	runViewSchema,
	uploadTicketSchema,
	wireClassificationRuleSchema,
} from '../schemas';
import { keys } from './keys';

export function useIntakeQueueQuery(module: ModuleCode) {
	return useQuery({
		queryKey: keys.intake.queue(module),
		queryFn: async () =>
			parsed(
				z.array(intakeQueueEntrySchema),
				await intakeApi.getIntakeQueue(module),
				`/intake/queue?module=${module}`,
			),
	});
}

export function useClientQuery(clientId: string | null) {
	return useQuery({
		queryKey: ['clients', clientId ?? ''] as const,
		queryFn: async () =>
			parsed(
				clientRecordSchema,
				await intakeApi.getClient(clientId as string),
				`/clients/${clientId}`,
			),
		enabled: Boolean(clientId),
	});
}

export function useFileQuery(
	fileId: string | null,
	/** Re-read every 1.5s while this holds for the file's status — e.g. while
	 *  its rows are still being prepared — and stop once it does not. */
	pollWhile?: (status: string) => boolean,
) {
	return useQuery({
		queryKey: keys.files.detail(fileId ?? ''),
		queryFn: async () =>
			parsed(
				fileRecordSchema,
				await intakeApi.getFile(fileId as string),
				`/files/${fileId}`,
			),
		enabled: Boolean(fileId),
		refetchInterval: (query) => {
			const status = query.state.data?.status;
			return pollWhile && status && pollWhile(status) ? 1500 : false;
		},
	});
}

/** A file as one audit sees it: 404 unless the file is in that audit. */
export function useAuditFileQuery(
	auditId: string | null,
	fileId: string | null,
) {
	return useQuery({
		queryKey: ['audits', auditId ?? '', 'files', fileId ?? ''] as const,
		queryFn: async () =>
			parsed(
				fileRecordSchema,
				await intakeApi.getAuditFile(
					auditId as string,
					fileId as string,
				),
				`/audits/${auditId}/files/${fileId}`,
			),
		enabled: Boolean(auditId && fileId),
	});
}

/**
 * The same file, for the screen that also needs to know when it was classified.
 *
 * Deliberately a different query key from `useFileQuery`: the two parse the
 * same response with different schemas, and sharing a key would mean whichever
 * mounted first decided what the other one saw.
 */
export function useFileWithClassifiedAtQuery(fileId: string | null) {
	return useQuery({
		queryKey: [
			...keys.files.detail(fileId ?? ''),
			'classified-at',
		] as const,
		queryFn: async () =>
			parsed(
				fileWithClassifiedAtSchema,
				await intakeApi.getFile(fileId as string),
				`/files/${fileId}`,
			),
		enabled: Boolean(fileId),
	});
}

/** The file's sheet columns and their mapping; fetched only while a window
 *  showing them is open. */
export function useFileColumnsQuery(fileId: string, enabled: boolean) {
	return useQuery({
		queryKey: keys.files.columns(fileId),
		queryFn: async () =>
			parsed(
				fileColumnsSchema,
				await intakeApi.getFileColumns(fileId),
				`/files/${fileId}/columns`,
			),
		enabled: Boolean(fileId) && enabled,
	});
}

/**
 * Sample rows.
 *
 * Only once bytes have been normalised into Parquet — the endpoint 404s
 * otherwise, which is why this is gated on the file being READY rather than
 * merely existing.
 */
export function useFileSampleQuery(fileId: string | null, ready: boolean) {
	return useQuery({
		queryKey: keys.files.sample(fileId ?? ''),
		queryFn: async () =>
			parsed(
				fileSampleSchema,
				await intakeApi.getFileSample(fileId as string),
				`/files/${fileId}/sample`,
			),
		enabled: Boolean(fileId) && ready,
	});
}

/** Which rules this file was actually classified with. */
export function useFileClassificationRulesQuery(fileId: string | null) {
	return useQuery({
		queryKey: keys.files.classificationRules(fileId ?? ''),
		queryFn: async () =>
			parsed(
				fileClassificationRuleRecordSchema,
				await intakeApi.getFileClassificationRules(fileId as string),
				`/files/${fileId}/classification-rules`,
			),
		enabled: Boolean(fileId),
	});
}

/**
 * The firm's classification rules, for the re-apply picker.
 *
 * `status=ACTIVE` is load-bearing, not a default being spelled out: the
 * endpoint answers with the DRAFT ruleset otherwise, which is what an admin is
 * editing rather than what the engine runs. Offering draft rules here would
 * let someone pick a rule re-apply then refuses.
 */
export function useFirmClassificationRulesQuery(
	status = 'ACTIVE',
	module?: ModuleCode,
	enabled = true,
) {
	return useQuery({
		queryKey: keys.firm.classificationRules(status, module),
		enabled,
		queryFn: async () =>
			parsed(
				z.object({ rules: z.array(wireClassificationRuleSchema) }),
				await intakeApi.getFirmClassificationRules(status, module),
				'/firm/classification-rules',
			),
	});
}

/** What the rules would do to this file, before anything is written. */
export function useClassificationPreviewQuery(
	fileId: string | null,
	ruleIdsQuery: string,
	enabled: boolean,
) {
	return useQuery({
		queryKey: keys.files.classificationPreview(fileId ?? '', ruleIdsQuery),
		queryFn: async () => {
			const path = `/files/${fileId}/classification-preview${ruleIdsQuery ? `?${ruleIdsQuery}` : ''}`;
			return parsed(
				classificationPreviewSchema,
				await intakeApi.getClassificationPreview(
					fileId as string,
					ruleIdsQuery,
				),
				path,
			);
		},
		enabled: Boolean(fileId) && enabled,
	});
}

/** A processing run, re-read while it is still going. */
export function useProcessingRunQuery(runId: string | null, active: boolean) {
	return useQuery({
		queryKey: keys.processingRuns.detail(runId ?? ''),
		queryFn: async () =>
			parsed(
				runViewSchema,
				await intakeApi.getProcessingRun(runId as string),
				`/processing-runs/${runId}`,
			),
		enabled: Boolean(runId),
		/* Stops itself: once the run is no longer RUNNING there is nothing further
       to learn, and a screen that keeps asking is pure load on a database this
       product already makes wait. */
		refetchInterval: active ? 1500 : false,
	});
}

/**
 * The upload ingest for one file, re-read while it is still going.
 *
 * Keyed by FILE, not by run: after a reload the screen knows which file it is
 * looking at and nothing else, and "the newest ingest for this file" is the
 * question it can always ask. That is the whole reason the ingest became a row
 * — a run identified only by a value held in a component's state would be lost
 * by the refresh it exists to survive.
 */
export function useFileIngestQuery(fileId: string | null) {
	return useQuery({
		queryKey: keys.files.ingest(fileId ?? ''),
		queryFn: async () =>
			parsed(
				latestIngestSchema,
				await intakeApi.getFileIngest(fileId as string),
				`/files/${fileId}/ingest`,
			),
		enabled: Boolean(fileId),
		/* Stops itself once the run has settled. A stale run counts as settled —
       the server has already decided the worker is gone, and asking again will
       not bring it back. */
		refetchInterval: (query) => {
			const run = query.state.data?.run;
			if (!run) return 1500;
			const running = run.status === 'RUNNING' || run.status === 'QUEUED';
			return running && !run.stale ? 1500 : false;
		},
	});
}

/* ── mutations ──────────────────────────────────────────────────────────── */

/** Re-run classification, then refresh everything that describes the result. */
export function useReapplyClassificationMutation(fileId: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (ruleIds: string[]) =>
			parsed(
				reapplyResultSchema,
				await intakeApi.reapplyClassification(fileId, ruleIds),
				`/files/${fileId}/classify`,
			),
		onSuccess: () => {
			void client.invalidateQueries({
				queryKey: keys.files.detail(fileId),
			});
			void client.invalidateQueries({
				queryKey: keys.files.classificationRules(fileId),
			});
			void client.invalidateQueries({ queryKey: keys.clients.all });
		},
	});
}

/**
 * The three one-shot verbs a file has. Same shape, same invalidation, so they
 * are one hook with three names rather than three copies of it.
 */
function useFileVerb(fileId: string, verb: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (body?: unknown) =>
			intakeApi.postFileVerb(fileId, verb, body),
		onSuccess: () => {
			void client.invalidateQueries({
				queryKey: keys.files.detail(fileId),
			});
			void client.invalidateQueries({
				queryKey: keys.intake.queueAnyModule(),
			});
		},
	});
}

export const useRetryFileMutation = (fileId: string) =>
	useFileVerb(fileId, 'retry');
export const useRequestReplacementMutation = (fileId: string) =>
	useFileVerb(fileId, 'request-replacement');
export const useExcludeFileMutation = (fileId: string) =>
	useFileVerb(fileId, 'exclude');

/** Delete a file (soft delete). It leaves every list, group and count, so
 *  those are all refreshed; the file's own page is dropped from the cache. */
export function useDeleteFileMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (fileId: string) => intakeApi.deleteFile(fileId),
		onSuccess: (_result, fileId) => {
			client.removeQueries({ queryKey: keys.files.detail(fileId) });
			void client.invalidateQueries({ queryKey: keys.clients.all });
			void client.invalidateQueries({ queryKey: ['file-groups'] });
			void client.invalidateQueries({
				queryKey: keys.intake.queueAnyModule(),
			});
			void client.invalidateQueries({ queryKey: ['dashboard'] });
			void client.invalidateQueries({ queryKey: keys.audits.all });
			/* The new-audit wizard's preview counts the client's files. */
			void client.invalidateQueries({
				queryKey: ['analyses', 'availability'],
			});
		},
	});
}

/**
 * Add one file to the library: presign, transfer to storage, then tell the API
 * the bytes arrived.
 *
 * All three steps in one mutation because they are one act — a ticket with no
 * upload behind it is litter, and an upload nothing confirms is invisible. The
 * phase and progress callbacks are inputs rather than state here: the wizard
 * uploads several files at once and tracks each row separately, which a single
 * mutation's own `isPending` could never describe.
 */
export function useUploadFileMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			clientId: string;
			fileName: string;
			file: File;
			sourceSystem?: string;
			periodLabel?: string;
			module: ModuleCode;
			onPhase: (phase: 'uploading' | 'reading') => void;
			onProgress: (percent: number) => void;
		}) => {
			input.onPhase('uploading');
			const ticket = parsed(
				uploadTicketSchema,
				await intakeApi.createUploadTicket({
					clientId: input.clientId,
					fileName: input.fileName,
					contentType: input.file.type || 'application/octet-stream',
					sourceSystem: input.sourceSystem,
					periodLabel: input.periodLabel || undefined,
					module: input.module,
				}),
				'/files/upload-url',
			);

			await putToStorage(ticket.uploadUrl, input.file, input.onProgress);

			/* 'reading' starts here and does NOT end when this call returns. The
         call starts an ingest run and comes straight back; the reading is
         Python's, on a background thread, and the row leaves this phase when
         `IngestProgress` says the run settled. Treating the response as
         completion is what the old blocking upload did, and it is exactly the
         claim that stopped being true. */
			input.onPhase('reading');
			return parsed(
				fileRecordSchema,
				await intakeApi.completeUpload(ticket.fileId),
				`/files/${ticket.fileId}/upload-complete`,
			);
		},
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: keys.files.all });
			void client.invalidateQueries({
				queryKey: keys.intake.queueAnyModule(),
			});
			void client.invalidateQueries({ queryKey: keys.clients.all });
		},
	});
}

/**
 * Save the chosen rules against the file, then start processing.
 *
 * Two calls in one act: choosing rules and not running them leaves a file
 * configured but untouched, which is not what the button says.
 */
export function useStartProcessingMutation(fileId: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (ruleIds: string[]) => {
			await intakeApi.setFileClassificationRules(fileId, ruleIds);
			return parsed(
				z.object({ runId: z.string() }),
				await intakeApi.startFileProcessing(fileId),
				`/files/${fileId}/process`,
			);
		},
		onSuccess: () => {
			void client.invalidateQueries({
				queryKey: keys.files.detail(fileId),
			});
			void client.invalidateQueries({
				queryKey: keys.intake.queueAnyModule(),
			});
		},
	});
}

/**
 * H2R: apply a file's confirmed mapping again, for a file whose processing did
 * not finish. The same endpoint as classification, which for an H2R file starts
 * the normalise instead and answers its run.
 */
export function useProcessAgainMutation(fileId: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async () =>
			parsed(
				z.object({ normaliseRunId: z.string() }),
				await intakeApi.startFileProcessing(fileId),
				`/files/${fileId}/process`,
			),
		onSuccess: () => {
			void client.invalidateQueries({
				queryKey: keys.files.detail(fileId),
			});
			void client.invalidateQueries({
				queryKey: keys.intake.queueAnyModule(),
			});
		},
	});
}

/** A file's timeline — what the history panel prints. */
export function useFileEventsQuery(fileId: string | null) {
	return useQuery({
		queryKey: keys.files.events(fileId ?? ''),
		queryFn: async () =>
			parsed(
				z.array(fileEventSchema),
				await intakeApi.getFileEvents(fileId as string),
				`/files/${fileId}/events`,
			),
		enabled: Boolean(fileId),
	});
}
